import { supabase } from "@/integrations/supabase/client";
import type { QuizEditData } from "@/components/quizzes/QuizEditModal";
import type { Quiz } from "./types";

export async function getQuizzes(userId: string, contentId?: string | null): Promise<Quiz[]> {
  let query = supabase
    .from("quizzes")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });

  if (contentId) {
    query = query.eq("content_id", contentId);
  }

  const { data, error } = await query;

  if (error) throw error;
  return (data || []) as Quiz[];
}

export async function deleteQuiz(quizId: string): Promise<void> {
  const { error } = await supabase
    .from("quizzes")
    .delete()
    .eq("id", quizId);

  if (error) throw error;
}

export async function updateQuizTitle(quizId: string, title: string): Promise<void> {
  const { error } = await supabase
    .from("quizzes")
    .update({ title })
    .eq("id", quizId);

  if (error) throw error;
}

export async function regenerateQuiz(quizId: string, data: QuizEditData): Promise<{ quiz_id: string; question_count: number }> {
  const response = await supabase.functions.invoke("regenerate-quiz", {
    body: {
      quiz_id: quizId,
      question_types: data.questionTypes,
      difficulty: data.difficulty,
      question_count: data.questionCount,
      topics: data.topics,
      instructions: data.instructions,
    },
  });

  if (response.error) {
    throw new Error(response.error.message || "Failed to regenerate quiz");
  }

  return response.data;
}

export async function restartQuiz(quizId: string): Promise<void> {
  // Get all attempts for this quiz
  const { data: attempts, error: attemptsError } = await supabase
    .from("quiz_attempts")
    .select("id")
    .eq("quiz_id", quizId);

  if (attemptsError) throw attemptsError;

  // Delete all attempt answers for each attempt
  if (attempts && attempts.length > 0) {
    const attemptIds = attempts.map((a) => a.id);
    
    const { error: answersError } = await supabase
      .from("quiz_attempt_answers")
      .delete()
      .in("attempt_id", attemptIds);

    if (answersError) throw answersError;

    // Delete all attempts
    const { error: deleteAttemptsError } = await supabase
      .from("quiz_attempts")
      .delete()
      .eq("quiz_id", quizId);

    if (deleteAttemptsError) throw deleteAttemptsError;
  }

  // Reset quiz score data
  const { error: updateError } = await supabase
    .from("quizzes")
    .update({
      last_attempted_at: null,
      last_score: null,
      last_total_correct: null,
      last_total_questions: null,
    })
    .eq("id", quizId);

  if (updateError) throw updateError;
}
